import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";

import { useOperations, useOperationsList } from "../hooks/useOperations";
import { OperationItem } from "./OperationItem";

// MAX_POLL_RUNNING limita quantos items `running` fazem polling 1s ao
// mesmo tempo. Acima disso, o item ainda renderiza com o snapshot da lista
// (refetch 5s do useOperations). Previne fan-out de N req/s no BFF quando
// o operador dispara muitas reidratações. 3 cobre o caso típico de
// "quero ver o progresso da última que disparei".
const MAX_POLL_RUNNING = 3;

// Debounce do search input — 300ms dá tempo pro usuário colar/digitar o
// UUID completo antes de disparar a query. Sem isso, cada keystroke de um
// UUID de 36 chars seria 36 queries HTTP.
const SEARCH_DEBOUNCE_MS = 300;

// Regex UUID v4 (mesmo padrão do BatchTicketsForm). Usada pra soft-validate
// o input antes de aplicar o filtro — se inválido, não dispara query e
// mostra warning inline, lista fica com filtro anterior.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function OperationsList() {
  const [filterInput, setFilterInput] = useState("");
  const [filterApplied, setFilterApplied] = useState("");
  const [includeRejected, setIncludeRejected] = useState(false);

  // Debounce: só aplica o filter após usuário parar de digitar. Só dispara
  // se o trimmed input é UUID válido OU vazio (= remove filtro).
  useEffect(() => {
    const trimmed = filterInput.trim();
    const handle = window.setTimeout(() => {
      if (trimmed === "") {
        setFilterApplied("");
      } else if (UUID_RE.test(trimmed)) {
        setFilterApplied(trimmed.toLowerCase());
      }
      // Input inválido → mantém filterApplied atual, UI mostra warning.
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [filterInput]);

  const query = useOperations({
    ticketId: filterApplied || undefined,
    includeRejected,
  });
  const { items } = useOperationsList({
    ticketId: filterApplied || undefined,
    includeRejected,
  });

  const isLoading = query.isLoading;
  const isError = query.isError;
  const trimmed = filterInput.trim();
  const hasInvalidInput = trimmed !== "" && !UUID_RE.test(trimmed);

  return (
    <Box component="section" aria-label="Operações recentes">
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Operações recentes
          {items.length > 0 ? ` (${items.length})` : ""}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeRejected}
                onChange={(e) => setIncludeRejected(e.target.checked)}
                inputProps={{ "aria-label": "Mostrar rejeitados" }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Mostrar rejeitados
              </Typography>
            }
            sx={{ mr: 0 }}
          />
          {/* Slot fixo pro spinner — sem isso, aparecer/sumir do
              CircularProgress reflowa o Stack e empurra o switch. */}
          <Box
            sx={{
              width: 16,
              height: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {query.isFetching && !isLoading ? (
              <CircularProgress size={16} aria-label="Atualizando lista" />
            ) : null}
          </Box>
        </Stack>
      </Stack>

      {/* Search input — sempre visível. Filtra por UUID do ticket. */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Filtrar por ticketID (UUID)"
          value={filterInput}
          onChange={(e) => setFilterInput(e.target.value)}
          error={hasInvalidInput}
          helperText={
            hasInvalidInput
              ? "UUID inválido — lista não será filtrada até digitar um UUID válido"
              : filterApplied
                ? `Filtrando por ticket: ${filterApplied}`
                : " "
          }
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">🔍</InputAdornment>
              ),
              endAdornment: filterInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Limpar filtro"
                    onClick={() => setFilterInput("")}
                  >
                    ✕
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </Box>

      {isLoading ? (
        <Stack spacing={2} aria-label="Carregando operações">
          {[0, 1, 2].map((idx) => (
            <Skeleton key={idx} variant="rounded" height={96} />
          ))}
        </Stack>
      ) : isError ? (
        <Stack spacing={1} alignItems="flex-start">
          <Alert severity="error" role="alert" sx={{ width: "100%" }}>
            Falha ao carregar operações
            {query.error?.message ? `: ${query.error.message}` : ""}
          </Alert>
          <Button
            size="small"
            variant="outlined"
            onClick={() => query.refetch()}
            sx={{ textTransform: "none" }}
          >
            Tentar novamente
          </Button>
        </Stack>
      ) : items.length === 0 ? (
        <Stack spacing={0.5}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {filterApplied
              ? "Nenhuma reidratação encontrada para este ticket"
              : "Nenhuma reidratação ainda"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filterApplied
              ? "Verifique se o UUID está correto ou limpe o filtro (✕) pra ver tudo."
              : "Use os formulários acima pra disparar a primeira operação. O histórico fica salvo e aparece aqui pra todos que tiverem acesso."}
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2} role="list">
          {(() => {
            let runningWithPoll = 0;
            return items.map((op) => {
              const isRunning = op.status === "running";
              const enablePolling =
                isRunning && runningWithPoll < MAX_POLL_RUNNING;
              if (isRunning && enablePolling) runningWithPoll++;
              return (
                <OperationItem
                  key={op.trackingId}
                  op={op}
                  enablePolling={enablePolling}
                />
              );
            });
          })()}
          {query.hasNextPage ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                sx={{ textTransform: "none" }}
              >
                {query.isFetchingNextPage ? "Carregando..." : "Carregar mais"}
              </Button>
            </Box>
          ) : null}
        </Stack>
      )}
    </Box>
  );
}
